import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, User, ChevronDown, ChevronUp, LogOut, Settings, Package, BookOpen } from "lucide-react";
import { navigationLinks, otherLinks } from "@/constants";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuthorRedirect } from '@/hooks/useAuthorRedirect';
import { useLogoutDialog } from '../Layout';

interface NavbarMobileProps {
  isDarkTheme: boolean;
  isHomePage: boolean;
}

const NavbarMobile = ({ isDarkTheme, isHomePage }: NavbarMobileProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([]);
  const location = useLocation();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { goToAuthorPlatform } = useAuthorRedirect();
  const { setShowLogoutDialog } = useLogoutDialog();

  const toggleDropdown = (name: string) => {
    if (openDropdowns.includes(name)) {
      setOpenDropdowns(openDropdowns.filter(item => item !== name));
    } else {
      setOpenDropdowns([...openDropdowns, name]);
    }
  };

  const isChildActive = (items: { path: string }[] | undefined) => {
    if (!items) return false;
    return items.some(item => location.pathname === item.path);
  };

  const getNavTranslationKey = (name: string) => {
    const key = name.toLowerCase().replace(/\s+/g, '');

    if (t(`nav.${key}`) !== key) {
      return `nav.${key}`;
    }

    switch (key) {
      case 'home':
        return 'nav.home';
      case 'library':
        return 'nav.library';
      case 'player':
        return 'nav.player';
      case 'storeowner':
        return 'nav.storeowner';
      case 'author':
        return 'nav.author';
      case 'helpcenter':
        return 'nav.help';
      case 'community':
        return 'nav.community';
      case 'blog':
        return 'nav.blog';
      case 'aboutus':
        return 'nav.about';
      case 'careers':
        return 'nav.careers';
      case 'other':
        return 'nav.more';
      default:
        return `nav.${key}`;
    }
  };

  const handleCustomRedirect = (type: string) => {
    if (type === 'author') {
      setIsMenuOpen(false);
      goToAuthorPlatform();
    }
  };

  const handleLogoutClick = () => {
    setIsMenuOpen(false);
    setShowLogoutDialog(true);
  };

  return (
    <>
      <div className="md:hidden">
        <div className="flex items-center">
          <LanguageSwitcher isDarkTheme={isDarkTheme} />

          {isAuthenticated ? (
            <button
              onClick={() => toggleDropdown("UserMenu")}
              className="mx-4"
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.avatar} alt={user?.name} />
                <AvatarFallback className="bg-anzir/20 text-anzir text-xs">
                  {user?.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </button>
          ) : (
            <Link
              to="/login"
              className={`flex items-center gap-2 mx-3 px-3 py-1.5 rounded-full ${
                isDarkTheme 
                  ? "bg-white/10 text-white hover:bg-white/20" 
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200"
              } transition-all`}
            >
              <User size={16} />
              <span className="text-sm font-medium">{t('nav.login', 'Log In')}</span>
            </Link>
          )}

          <button
            className={`${isDarkTheme ? "text-white" : "text-gray-900"}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isMenuOpen && (
          <motion.div
            className={`fixed top-0 left-0 w-full h-screen ${
              isDarkTheme ? "bg-black/80" : "bg-white/90"
            } backdrop-blur-md z-50 flex flex-col items-center justify-start pt-16 pb-8 gap-6 overflow-y-auto`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {/* Close button at the top */}
            <button
              className={`absolute top-4 right-4 ${isDarkTheme ? "text-white" : "text-gray-900"}`}
              onClick={() => setIsMenuOpen(false)}
            >
              <X className="h-6 w-6" />
            </button>

            {/* User profile section for mobile */}
            {isAuthenticated && (
              <div className="w-full max-w-md px-4 mb-2">
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 flex items-center">
                  <Avatar className="w-10 h-10 mr-3">
                    <AvatarImage src={user?.avatar} alt={user?.name} />
                    <AvatarFallback className="bg-anzir/20 text-anzir">
                      {user?.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-white font-medium">{user?.name}</p>
                    <p className="text-gray-400 text-sm">{user?.email}</p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <Link
                    to="/profile"
                    className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-center text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-base">Profile</span>
                  </Link>

                  <Link
                    to="/profile/games"
                    className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-center text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <BookOpen className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-base">My Games</span>
                  </Link>

                  <Link
                    to="/profile/orders"
                    className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-center text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Package className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-base">Orders</span>
                  </Link>

                  <Link
                    to="/profile/settings"
                    className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-center text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Settings className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-base">Settings</span>
                  </Link>
                </div>

                <button
                  onClick={handleLogoutClick}
                  className="mt-4 w-full py-2 px-4 bg-red-900/20 text-red-500 rounded-lg flex items-center justify-center"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  Logout
                </button>
              </div>
            )}

            {/* Login button for non-authenticated users */}
            {!isAuthenticated && (
              <div className="w-full max-w-md px-4 mb-6">
                <Link
                  to="/login"
                  className="w-full py-3.5 px-4 bg-gradient-to-r from-[#F97316] to-[#FF9800] text-white rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20 transition-all hover:shadow-orange-500/30 hover:translate-y-[-2px]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User className="w-5 h-5 mr-2" />
                  <span className="font-medium">{t('nav.login', 'Log In')}</span>
                </Link>
              </div>
            )}

            <nav className="flex flex-col items-center gap-5 w-full max-w-md px-4">
              {navigationLinks.map((link) => (
                <div key={link.name} className="w-full flex flex-col items-center">
                  {link.dropdown ? (
                    <>
                      <button
                        className={`${
                          isChildActive(link.items)
                            ? `${isDarkTheme ? "text-white" : "text-gray-900"} text-base font-medium border-b-2 border-[#F97316] pb-1`
                            : isDarkTheme 
                              ? "text-white/80 hover:text-white" 
                              : "text-gray-700 hover:text-gray-900"
                        } transition-colors flex items-center gap-1`}
                        onClick={() => toggleDropdown(link.name)}
                      >
                        {t(getNavTranslationKey(link.name))}
                        {openDropdowns.includes(link.name) ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </button>

                      {openDropdowns.includes(link.name) && link.items && (
                        <div className="flex flex-col items-center gap-3 mt-3 mb-2">
                          {link.items.map((item) => (
                            <NavLink
                              key={item.name}
                              to={item.path}
                              className={({ isActive }) =>
                                isActive
                                  ? `${isDarkTheme ? "text-white" : "text-gray-900"} text-base font-medium border-b-2 border-[#F97316] pb-1`
                                  : `${isDarkTheme ? "text-white/80" : "text-gray-700"} text-base hover:${isDarkTheme ? "text-white" : "text-gray-900"} transition-colors`
                              }
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {t(getNavTranslationKey(item.name))}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </>
                  ) : link.external ? (
                    link.customRedirect ? (
                      <button
                        className={`${isDarkTheme ? "text-white/80" : "text-gray-700"} text-base hover:${isDarkTheme ? "text-white" : "text-gray-900"} transition-colors`}
                        onClick={() => {
                          handleCustomRedirect(link.customRedirect);
                        }}
                      >
                        {t(getNavTranslationKey(link.name))}
                      </button>
                    ) : (
                      <a
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${isDarkTheme ? "text-white/80" : "text-gray-700"} text-base hover:${isDarkTheme ? "text-white" : "text-gray-900"} transition-colors`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {t(getNavTranslationKey(link.name))}
                      </a>
                    )
                  ) : (
                    <NavLink
                      to={link.path}
                      className={({ isActive }) => {
                        const active = link.path === "/"
                          ? isHomePage
                          : isActive || (link.path === "/library" && location.pathname.startsWith("/script/"));
                        return active
                          ? `${isDarkTheme ? "text-white" : "text-gray-900"} text-base font-medium border-b-2 border-[#F97316] pb-1`
                          : `${isDarkTheme ? "text-white/80" : "text-gray-700"} text-base hover:${isDarkTheme ? "text-white" : "text-gray-900"} transition-colors`;
                      }}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t(getNavTranslationKey(link.name))}
                    </NavLink>
                  )}
                </div>
              ))}

              <div className="w-full flex flex-col items-center">
                <button
                  className={`${
                    otherLinks.some(link => location.pathname === link.path)
                      ? `${isDarkTheme ? "text-white" : "text-gray-900"} text-base font-medium border-b-2 border-[#F97316] pb-1`
                      : `${isDarkTheme ? "text-white/80" : "text-gray-700"} text-base hover:${isDarkTheme ? "text-white" : "text-gray-900"} transition-colors`
                  } flex items-center gap-1`}
                  onClick={() => toggleDropdown("Other")}
                >
                  {t('nav.more')}
                  {openDropdowns.includes("Other") ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>

                {openDropdowns.includes("Other") && (
                  <div className="flex flex-col items-center gap-3 mt-3">
                    {otherLinks.map((link) => (
                      <div key={link.name} className="w-full flex flex-col items-center">
                        <NavLink
                          to={link.path}
                          className={({ isActive }) =>
                            isActive
                              ? `${isDarkTheme ? "text-white" : "text-gray-900"} text-base font-medium border-b-2 border-[#F97316] pb-1`
                              : `${isDarkTheme ? "text-white/80" : "text-gray-700"} text-base hover:${isDarkTheme ? "text-white" : "text-gray-900"} transition-colors`
                          }
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {t(getNavTranslationKey(link.name))}
                        </NavLink>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default NavbarMobile;
